import { Button, Modal, Tabs } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { type ApiError } from "../../../api";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import type { Site } from "../../../utils/backend-types";
import { AutoImportNotificationMsg } from "../AutoImportNotificationMsg/AutoImportNotificationMsg";
import { HistoricalDataImportWithSubmit } from "../HistoricalDataImport/HistoricalDataImportWithSubmit";
import { updateMeterParametersCache } from "../hooks/useMeterParameters";
import { useSubMeteringConfigurationMutations } from "../hooks/useSubMeteringConfigurationMutations";
import { MeterConfiguration } from "../MeterConfiguration/MeterConfiguration";
import { MetersSaveWarningModal } from "../MeterConfiguration/MetersSaveWarningModal/MetersSaveWarningModal";
import {
  SUB_METERING_FORM_ID,
  SubMeteringConfigurationForm,
  SubmeteringConfigurationFormMode
} from "../SubMeteringConfigurationForm/SubMeteringConfigurationForm";
import type { ConfigurationData } from "../ThirdPartySystems.types";
import { checkSubMeteringConnection } from "../utils/checkSubMeteringConnection";
import { getConfigurationDisplayName } from "../utils/getConfigurationDisplayName";
import { showSaveMeterErrorToast } from "../utils/showSaveMeterErrorToast";
import { updateEntitiesMeterData } from "../utils/updateEntitiesMeterData";

const CONFIGURATION_FORM_TAB = "verbindung";
const METER_SELECTION_TAB = "zähler";
const HISTORICAL_DATA_IMPORT_TAB = "import";
export const DEFAULT_SUBPAGE = CONFIGURATION_FORM_TAB;

export interface SelectedMeter {
  subMeteringSystemEntityId: number;
  meterId: number;
  meterName: string;
  parameterName: string;
  isSuggested?: boolean;
}

type DifferentParamsInfo = Array<Record<string, Array<string>>>;

interface SaveMeterErrorData {
  extraInfo?: {
    meterName: string;
    differingParameters: DifferentParamsInfo;
  };
  error?: string;
  detail?: string;
}

interface EditConfigurationModalProps {
  isOpen: boolean;
  configuration: ConfigurationData;
  sites: Array<Site>;
  variantId: number;
  onToggle: () => void;
  onConfigurationUpdated: (configuration: ConfigurationData) => void;
}

function EditConfigurationModal({
  isOpen,
  configuration,
  sites,
  variantId,
  onToggle,
  onConfigurationUpdated
}: EditConfigurationModalProps) {
  const navigate = useNavigate();
  const { showToast, showErrorToast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { subPage: activeTab } = useParams();

  const [connectionLoading, setConnectionLoading] = useState(false);
  const [
    isSelectedMetersWarningModalOpen,
    setIsSelectedMetersWarningModalOpen
  ] = useState<boolean>(false);
  const [selectedMeters, setSelectedMeters] = useState<Array<SelectedMeter>>(
    []
  );
  const { updateSubMeteringConfigurationMutation } =
    useSubMeteringConfigurationMutations(variantId);

  async function handleCheckSubMeteringConnection({ withSuccessToast = true }) {
    try {
      setConnectionLoading(true);
      const checkConnectionRes = await checkSubMeteringConnection(
        configuration.id
      );

      if (checkConnectionRes.data?.error) {
        throw new Error(
          checkConnectionRes.data?.error ||
            t("errors.ThirdPartySystems.AuthenticationFailed")
        );
      }

      if (withSuccessToast) {
        showToast({ type: "success", message: "Verbindung erfolgreich" });
      }

      return true;
    } catch (error) {
      showErrorToast(
        error?.message || t("errors.ThirdPartySystems.AuthenticationFailed")
      );
    } finally {
      setConnectionLoading(false);
    }
  }

  const handleSaveMeters = useCallback(
    async (singleEntityId?: number) => {
      try {
        if (singleEntityId) {
          const singleSelectedMeter = selectedMeters.find(
            (selectedMeter) =>
              selectedMeter.subMeteringSystemEntityId === singleEntityId
          );

          const meterId = singleSelectedMeter?.meterId ?? null;

          await updateEntitiesMeterData(configuration.id, [
            {
              entityId: singleEntityId,
              meterId: meterId
            }
          ]);

          setSelectedMeters((prevSelectedMeters) => {
            return prevSelectedMeters.filter(
              (meter) => meter.subMeteringSystemEntityId !== singleEntityId
            );
          });

          updateMeterParametersCache(
            singleEntityId,
            { meter: meterId },
            queryClient,
            configuration.id
          );

          return { success: true };
        }

        await updateEntitiesMeterData(
          configuration.id,
          selectedMeters.map((selectedMeter) => ({
            entityId: selectedMeter.subMeteringSystemEntityId,
            meterId: selectedMeter.meterId
          }))
        );

        showToast({
          type: "success",
          message: (
            <AutoImportNotificationMsg selectedMeters={selectedMeters} />
          ),
          autoClose: false,
          withCloseButton: true
        });

        setSelectedMeters([]);

        return { success: true };
      } catch (error) {
        const err = error as ApiError<SaveMeterErrorData>;
        showSaveMeterErrorToast(showToast, err);

        return { success: false };
      }
    },
    [configuration.id, queryClient, selectedMeters, showToast]
  );

  async function handleSubmitSubMeteringConfigurationForm(
    data: ConfigurationData
  ) {
    const responseData = (
      await updateSubMeteringConfigurationMutation.mutateAsync({
        data,
        id: configuration.id
      })
    ).data;

    onConfigurationUpdated(responseData);
    onToggle();

    return responseData;
  }

  function handleCloseSelectedMetersSaveModal() {
    setIsSelectedMetersWarningModalOpen(false);
  }

  function handleModalClose() {
    if (selectedMeters.length > 0) {
      setIsSelectedMetersWarningModalOpen(true);
      return;
    }

    onToggle();
  }

  return (
    <Modal
      className="EditConfigurationModal"
      opened={isOpen}
      size={activeTab === METER_SELECTION_TAB ? "min(90vw, 1600px)" : undefined}
      title={`${getConfigurationDisplayName(
        configuration.name,
        configuration.subMeteringSystem
      )} bearbeiten`}
      onClose={handleModalClose}
    >
      <Tabs value={activeTab} onChange={(newTab) => navigate(`../${newTab}`)}>
        <Tabs.List>
          <Tabs.Tab component="a" value={CONFIGURATION_FORM_TAB}>
            Verbindung
          </Tabs.Tab>
          <Tabs.Tab component="a" value={METER_SELECTION_TAB}>
            Zählerzuordnung
          </Tabs.Tab>
          <Tabs.Tab component="a" value={HISTORICAL_DATA_IMPORT_TAB}>
            Import
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value={CONFIGURATION_FORM_TAB}>
          <SubMeteringConfigurationForm
            initialConfigurationData={configuration}
            mode={SubmeteringConfigurationFormMode.Edit}
            variantId={variantId}
            onSubmit={handleSubmitSubMeteringConfigurationForm}
          />
        </Tabs.Panel>
        <Tabs.Panel value={METER_SELECTION_TAB}>
          <MeterConfiguration
            configurationId={configuration.id}
            connectionLoading={connectionLoading}
            selectedMeters={selectedMeters}
            setSelectedMeters={setSelectedMeters}
            sites={sites}
            systemName={configuration.subMeteringSystem}
            onMetersSave={handleSaveMeters}
            onSubMeteringConnectionCheck={handleCheckSubMeteringConnection}
          />

          <MetersSaveWarningModal
            isOpen={isSelectedMetersWarningModalOpen}
            onCloseEditModal={onToggle}
            onCloseSelectedMetersWarningModal={
              handleCloseSelectedMetersSaveModal
            }
          />
        </Tabs.Panel>
        <Tabs.Panel value={HISTORICAL_DATA_IMPORT_TAB}>
          <HistoricalDataImportWithSubmit id={configuration.id} />
        </Tabs.Panel>
      </Tabs>
      <ModalFooter>
        {activeTab === CONFIGURATION_FORM_TAB && (
          <>
            <Button
              color="primary"
              loading={connectionLoading}
              onClick={() =>
                handleCheckSubMeteringConnection({ withSuccessToast: true })
              }
            >
              Verbindung testen
            </Button>
          </>
        )}
        <Button
          color="secondary"
          form={SUB_METERING_FORM_ID}
          loading={updateSubMeteringConfigurationMutation.isPending}
          type="submit"
        >
          Speichern
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { EditConfigurationModal, EditConfigurationModalProps };
